import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import { ArrowRight, MagnifyingGlass } from "@phosphor-icons/react";

export default function LearnHub() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("All");
  const [recommendation, setRecommendation] = useState(null);

  useEffect(() => {
    api.get("/education").then((r) => setItems(r.data.items || [])).catch(() => {});
    // Personalize based on the user's quiz recommendation (saved at signup or quiz submit)
    try {
      const raw = localStorage.getItem("ex_user_recommendation");
      if (raw) setRecommendation(JSON.parse(raw));
    } catch {}
  }, []);

  // Personalization: match on the new primary_topic field stored on each item
  // (set in seed_data.py). Falls back to category-string match if absent.
  const PRIMARY_TO_TOPIC = {
    iul: "iul",
    annuity: "annuity",
    whole_life: "whole_life",
  };
  const recommendedTopic = recommendation
    ? PRIMARY_TO_TOPIC[recommendation.primary]
    : null;

  const categories = ["All", ...Array.from(new Set(items.map((i) => i.category)))];

  // Sort: recommended topic first, then others
  const itemsSorted = [...items].sort((a, b) => {
    if (!recommendedTopic) return 0;
    const aMatch = a.primary_topic === recommendedTopic ? 0 : 1;
    const bMatch = b.primary_topic === recommendedTopic ? 0 : 1;
    return aMatch - bMatch;
  });

  const filtered = itemsSorted.filter(
    (i) =>
      (filter === "All" || i.category === filter) &&
      (search === "" ||
        i.title.toLowerCase().includes(search.toLowerCase()) ||
        i.summary.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div data-testid="learn-hub" className="min-h-screen bg-exodus-bg text-exodus-text">
      <Nav />
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pt-36 pb-12">
        <p className="eyebrow"><span className="gold-divider" />The Education Library</p>
        <h1 className="font-display text-5xl lg:text-7xl mt-4 leading-[0.95]">
          Read it. <span className="gold-text italic">Understand</span> it. Decide for yourself.
        </h1>
        <p className="text-lg text-exodus-muted mt-8 max-w-2xl leading-relaxed">
          Every concept worth knowing — written in plain English. No log-in. No download. No "tap your email to continue." Just the work, available to you.
        </p>
        {recommendation && (
          <div
            data-testid="learn-personalized-banner"
            className="mt-10 inline-flex items-center gap-3 border border-exodus-gold/60 bg-exodus-gold/10 px-5 py-3"
          >
            <span className="text-[10px] eyebrow text-exodus-gold">Personalized for you</span>
            <span className="text-sm">
              Based on your quiz, start with{" "}
              <strong className="text-exodus-gold">{recommendation.primary_label}</strong> — pinned to the top.
            </span>
          </div>
        )}
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
        <div className="flex flex-wrap gap-3 items-center mb-10">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-exodus-muted" />
            <input
              data-testid="learn-search"
              className="input-exodus pl-10"
              placeholder="Search topics — IUL, annuity, tax, retirement…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`learn-filter-${c.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => setFilter(c)}
                className={`text-xs px-4 py-2 border transition-colors ${
                  filter === c
                    ? "border-exodus-gold text-exodus-gold"
                    : "border-exodus-border text-exodus-muted hover:text-exodus-text"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-exodus-border">
          {filtered.map((t) => {
            const isRecommended = recommendedTopic && t.primary_topic === recommendedTopic;
            return (
              <Link
                key={t.slug}
                to={`/learn/${t.slug}`}
                data-testid={`learn-card-${t.slug}`}
                className={`group bg-exodus-bg p-8 hover:bg-exodus-surface transition-colors flex flex-col relative ${
                  isRecommended ? "ring-1 ring-exodus-gold/60" : ""
                }`}
              >
                {isRecommended && (
                  <span className="absolute top-3 right-3 text-[10px] uppercase tracking-wider gold-text">
                    ★ For You
                  </span>
                )}
                <p className="eyebrow text-xs">{t.category}</p>
                <h3 className="font-display text-2xl mt-3 leading-tight group-hover:text-exodus-gold transition-colors flex-1">
                  {t.title}
                </h3>
                <p className="text-sm text-exodus-muted mt-4 leading-relaxed line-clamp-3">{t.summary}</p>
                <div className="flex items-center gap-2 mt-6 text-xs gold-text">
                  Read the breakdown
                  <ArrowRight size={12} weight="bold" className="group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            );
          })}
        </div>

        {filtered.length === 0 && (
          <p className="text-center text-exodus-muted py-16">No topics match your search.</p>
        )}
      </section>

      <Footer />
    </div>
  );
}
